import { POPULATION_TYPE, SensitivityLevelHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { EntitiesModalProps } from "@/components/modals/entities-table-modal/model";
import { cord, cord3 } from "@/gql/lib";
import { AxisType, TimeAxisRangeInput } from "@/gql/types";

const from_cloud_folders = (
  label_id: string,
  z: TimeAxisRangeInput
): EntitiesModalProps => {
  return {
    info_types: [label_id],
    type: POPULATION_TYPE.FOLDER,
    title: `Cloud Folders containing  <info_type> information`,

    coordinate: {
      x: cord(AxisType.Folder),
      y: {
        type: AxisType.ContentLabel,
        values: [label_id],
      },
      z,
    },
    files_title: (entity, label_name) =>
      `<b>${label_name}</b> Information files that <b>${entity.name}</b> has access`,
    events: {
      title: (entity, label_name) =>
        `Events related to <b>${entity.name}</b> containing <b>${label_name}</b> information`,
      coordinate: (entity, other_label_id) => {
        return {
          x: cord3(AxisType.Folder, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: other_label_id ? [other_label_id] : [label_id],
          },
          z,
        };
      },
    },
  };
};

const from_employees = (
  label_id: string,
  z: TimeAxisRangeInput
): EntitiesModalProps => {
  return {
    info_types: [label_id],
    type: POPULATION_TYPE.EMPLOYEE,
    title: `Employees that shared <info_type> information`,

    coordinate: {
      x: cord(AxisType.EmployeeSender),
      y: {
        type: AxisType.ContentLabel,
        values: [label_id],
      },
      z,
    },
    files_title: (entity, label_name) =>
      `<b>${label_name}</b> files shared by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> shared <b>${label_name}</b> information`,
      coordinate: (entity, other_label_id) => {
        return {
          x: cord3(AxisType.EmployeeSender, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: other_label_id ? [other_label_id] : [label_id],
          },
          z,
        };
      },
    },
  };
};

const to_employees = (
  label_id: string,
  z: TimeAxisRangeInput
): EntitiesModalProps => {
  return {
    info_types: [label_id],
    type: POPULATION_TYPE.EMPLOYEE,
    title: `Employees that received  <info_type> information`,
    coordinate: {
      x: cord(AxisType.EmployeeReceiver),
      y: {
        type: AxisType.ContentLabel,
        values: [label_id],
      },
      z,
    },
    files_title: (entity, label_name) =>
      `<b>${label_name}</b> files received by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> received <b>${label_name}</b> information`,
      coordinate: (entity, other_label_id) => {
        return {
          x: cord3(AxisType.EmployeeReceiver, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: other_label_id ? [other_label_id] : [label_id],
          },
          z,
        };
      },
    },
  };
};

const to_external_access = (
  label_id: string,
  z: TimeAxisRangeInput
): EntitiesModalProps => {
  return {
    info_types: [label_id],
    type: POPULATION_TYPE.CONTACT,
    title: `External Contacts that received <info_type> information`,
    coordinate: {
      x: cord(AxisType.ExternalReceiver),
      y: {
        type: AxisType.ContentLabel,
        values: [label_id],
      },
      z,
    },
    files_title: (entity, label_name) =>
      `<b>${label_name}</b> files received by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> received <b>${label_name}</b> information`,
      coordinate: (entity, other_label_id) => {
        return {
          x: cord3(AxisType.ExternalReceiver, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: other_label_id ? [other_label_id] : [label_id],
          },
          z,
        };
      },
    },
  };
};

const to_organizations = (
  label_id: string,
  z: TimeAxisRangeInput
): EntitiesModalProps => {
  return {
    info_types: [label_id],
    type: POPULATION_TYPE.ORGANIZATION,
    title: `Organizations that received <info_type> information`,
    coordinate: {
      x: cord(AxisType.OrganizationReceiver),
      y: {
        type: AxisType.ContentLabel,
        values: [label_id],
      },
      z,
    },
    files_title: (entity, label_name) =>
      `<b>${label_name}</b> files received by <b>${entity.name}</b>`,
    events: {
      title: (entity, label_name) =>
        `Events where <b>${entity.name}</b> received <b>${label_name}</b> information`,
      coordinate: (entity, other_label_id) => {
        return {
          x: cord3(AxisType.OrganizationReceiver, [entity.id]),
          y: {
            type: AxisType.ContentLabel,
            values: other_label_id ? [other_label_id] : [label_id],
          },
          z,
        };
      },
    },
  };
};

export const get_modal_props = (
  label_id: string,
  z: TimeAxisRangeInput,
  type: AxisType
): EntitiesModalProps => {
  const d = {
    [AxisType.EmployeeReceiverType]: "",
  };
  switch (type) {
    case AxisType.FolderType:
      return from_cloud_folders(label_id, z);
    case AxisType.EmployeeReceiverType:
      return to_employees(label_id, z);
    case AxisType.OrganizationReceiverType:
      return to_organizations(label_id, z);
    case AxisType.EmployeeSenderType:
      return from_employees(label_id, z);
    case AxisType.ExternalReceiverType:
    default:
      return to_external_access(label_id, z);
  }
};
